'use client';

import { useState, FormEvent, useCallback } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import styles from './Contact.module.css';

const FORMSPREE_URL = 'https://formspree.io/f/mlgpdeoy';
const SUBMIT_TIMEOUT_MS = 10_000;

export default function Contact() {
    const [formData, setFormData] = useState({
        nama: '',
        email: '',
        telepon: '',
        perusahaan: '',
        layanan: '',
        pesan: '',
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = useCallback(async (e: FormEvent) => {
        e.preventDefault();
        if (submitting) return;

        setSubmitting(true);
        const toastId = toast.loading('Mengirim pesan...');
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), SUBMIT_TIMEOUT_MS);

        try {
            const res = await fetch(FORMSPREE_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    nama: formData.nama,
                    email: formData.email,
                    telepon: formData.telepon,
                    perusahaan: formData.perusahaan,
                    layanan: formData.layanan,
                    pesan: formData.pesan,
                }),
                signal: controller.signal,
            });

            if (!res.ok) {
                const body = await res.json().catch(() => null);
                throw new Error(body?.error ?? `Server responded with ${res.status}`);
            }

            toast.success('Pesan Anda telah terkirim! Tim kami akan segera menghubungi Anda.', {
                id: toastId,
                duration: 5000,
                style: { background: '#22c55e', color: '#fff', fontWeight: 500, borderRadius: '8px' },
                iconTheme: { primary: '#fff', secondary: '#22c55e' },
            });
            setFormData({ nama: '', email: '', telepon: '', perusahaan: '', layanan: '', pesan: '' });
        } catch (err) {
            const message =
                err instanceof DOMException && err.name === 'AbortError'
                    ? 'Koneksi timeout. Silakan coba lagi.'
                    : err instanceof Error
                        ? err.message
                        : 'Gagal mengirim pesan. Silakan coba lagi.';
            toast.error(message, {
                id: toastId,
                duration: 5000,
                style: { background: '#ef4444', color: '#fff', fontWeight: 500, borderRadius: '8px' },
            });
        } finally {
            clearTimeout(timeout);
            setSubmitting(false);
        }
    }, [formData, submitting]);

    return (
        <>
            {/* ═══════════ SECTION 1: Quick Contact Strip ═══════════ */}
            <section className={styles.quickStrip}>
                <div className="container">
                    <div className={styles.quickGrid}>
                        <a href="https://wa.me/628567874759" target="_blank" rel="noopener noreferrer" className={styles.quickCard}>
                            <div className={styles.quickIcon}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                            </div>
                            <div className={styles.quickText}>
                                <span className={styles.quickLabel}>WhatsApp</span>
                                <span className={styles.quickValue}>0856-7874-759</span>
                            </div>
                        </a>

                        <a href="tel:02716010777" className={styles.quickCard}>
                            <div className={styles.quickIcon}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                            </div>
                            <div className={styles.quickText}>
                                <span className={styles.quickLabel}>Telepon Kantor</span>
                                <span className={styles.quickValue}>0271-601-0777</span>
                            </div>
                        </a>

                        <div className={styles.quickCard} onClick={() => window.location.href = 'mai' + 'lto:' + 'info' + '@' + 'sipasifik.com'} role="button" tabIndex={0}>
                            <div className={styles.quickIcon}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22,7 12,13 2,7" /></svg>
                            </div>
                            <div className={styles.quickText}>
                                <span className={styles.quickLabel}>Email</span>
                                <span className={styles.quickValue}>info<span className={styles.hiddenText}>-null-</span>&#64;sipasifik.com</span>
                            </div>
                        </div>

                        <div className={styles.quickCard}>
                            <div className={styles.quickIcon}>
                                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                            </div>
                            <div className={styles.quickText}>
                                <span className={styles.quickLabel}>Customer Service</span>
                                <span className={styles.quickValue}>
                                    <span className={styles.liveDot} />
                                    24/7 Online
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════════ SECTION 2: Form + Info ═══════════ */}
            <section id="kontak" className={styles.mainSection}>
                <div className="container">
                    <div className={styles.layout}>
                        {/* ── Left: Form ── */}
                        <div className={styles.formCard}>
                            <div className={styles.formHeader}>
                                <h3 className={styles.formTitle}>Kirim Pesan</h3>
                                <p className={styles.formSubtitle}>
                                    Ceritakan kebutuhan logistik Anda dan tim kami akan merespon dalam 1×24 jam kerja.
                                </p>
                            </div>

                            <form className={styles.form} onSubmit={handleSubmit}>
                                <div className={styles.formRow}>
                                    <div className={styles.inputGroup}>
                                        <label className={styles.label} htmlFor="contact-nama">Nama Lengkap *</label>
                                        <input id="contact-nama" className={styles.input} type="text" placeholder="John Doe" required value={formData.nama} onChange={(e) => setFormData({ ...formData, nama: e.target.value })} />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label className={styles.label} htmlFor="contact-email">Email *</label>
                                        <input id="contact-email" className={styles.input} type="email" placeholder="email@perusahaan.com" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                                    </div>
                                </div>

                                <div className={styles.formRow}>
                                    <div className={styles.inputGroup}>
                                        <label className={styles.label} htmlFor="contact-telepon">Nomor Telepon</label>
                                        <input id="contact-telepon" className={styles.input} type="tel" placeholder="08xx xxxx xxxx" value={formData.telepon} onChange={(e) => setFormData({ ...formData, telepon: e.target.value })} />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label className={styles.label} htmlFor="contact-perusahaan">Perusahaan</label>
                                        <input id="contact-perusahaan" className={styles.input} type="text" placeholder="PT Example" value={formData.perusahaan} onChange={(e) => setFormData({ ...formData, perusahaan: e.target.value })} />
                                    </div>
                                </div>

                                <div className={styles.inputGroup}>
                                    <label className={styles.label} htmlFor="contact-layanan">Layanan yang Dibutuhkan</label>
                                    <select id="contact-layanan" className={styles.input} value={formData.layanan} onChange={(e) => setFormData({ ...formData, layanan: e.target.value })}>
                                        <option value="">Pilih layanan...</option>
                                        <option value="darat">Cargo Darat</option>
                                        <option value="laut">Cargo Laut</option>
                                        <option value="udara">Cargo Udara</option>
                                        <option value="warehouse">Warehousing</option>
                                        <option value="towing">Towing & Gendong</option>
                                        <option value="lainnya">Lainnya</option>
                                    </select>
                                </div>

                                <div className={styles.inputGroup}>
                                    <label className={styles.label} htmlFor="contact-pesan">Pesan *</label>
                                    <textarea id="contact-pesan" className={`${styles.input} ${styles.textarea}`} placeholder="Jelaskan detail kebutuhan pengiriman Anda..." required value={formData.pesan} onChange={(e) => setFormData({ ...formData, pesan: e.target.value })} />
                                </div>

                                <button type="submit" className={styles.submitBtn} disabled={submitting}>
                                    {submitting ? (
                                        <><span className={styles.spinner} /> Mengirim...</>
                                    ) : (
                                        <>
                                            Kirim Pesan
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>

                        {/* ── Right: Info Panel ── */}
                        <div className={styles.infoPanel}>
                            {/* Contact Info Card (compact) */}
                            <div className={styles.infoCard}>
                                <h3 className={styles.infoTitle}>Informasi Kontak</h3>
                                <p className={styles.infoSubtitle}>Kami siap melayani kebutuhan logistik Anda</p>

                                <div className={styles.infoList}>
                                    <div className={styles.infoItem}>
                                        <div className={styles.infoIcon}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                                        </div>
                                        <div>
                                            <div className={styles.infoLabel}>Telepon / WhatsApp</div>
                                            <div className={styles.infoValue}>
                                                <a href="https://wa.me/628567874759" target="_blank" rel="noopener noreferrer" className={styles.infoLink}>0856-7874-759</a>
                                                <span style={{ margin: '0 6px', opacity: 0.3 }}>|</span>
                                                <a href="tel:02716010777" className={styles.infoLink}>0271-601-0777</a>
                                            </div>
                                        </div>
                                    </div>

                                    <div className={styles.infoItem}>
                                        <div className={styles.infoIcon}>
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2" /><polyline points="22,7 12,13 2,7" /></svg>
                                        </div>
                                        <div>
                                            <div className={styles.infoLabel}>Email</div>
                                            <div className={styles.infoValue}>info<span className={styles.hiddenText}>-null-</span>&#64;sipasifik.com</div>
                                        </div>
                                    </div>

                                    <div className={styles.infoDivider} />

                                    <div className={styles.hoursSection}>
                                        <div className={styles.infoLabel}>Jam Operasional</div>
                                        <div className={styles.hoursGrid}>
                                            <div className={styles.hoursRow}><span>Senin — Jumat</span><span>08:00 — 18:00</span></div>
                                            <div className={styles.hoursRow}><span>Sabtu</span><span>08:00 — 14:00</span></div>
                                            <div className={styles.hoursRow}><span>Minggu & Libur</span><span className={styles.closed}>Tutup</span></div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Office Locations — each with its own map */}
                            <div className={styles.officeGrid}>
                                {/* Jakarta (Kantor Pusat) */}
                                <div className={styles.officeCard}>
                                    <div className={styles.officeMapWrap}>
                                        <iframe
                                            src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=Jl+Daan+Mogot+No+119+Kompleks+Aldiron+Jakarta+Barat&zoom=15"
                                            className={styles.mapIframe}
                                            allowFullScreen={false}
                                            loading="lazy"
                                            referrerPolicy="no-referrer-when-downgrade"
                                        />
                                    </div>
                                    <div className={styles.officeInfo}>
                                        <span className={styles.officeBadge}>Kantor Pusat</span>
                                        <h4 className={styles.officeCity}>Jakarta</h4>
                                        <p className={styles.officeAddress}>Jl. Daan Mogot No.119 Blok A No.12A, Kompleks Aldiron, Jakarta Barat, DKI Jakarta</p>
                                    </div>
                                </div>

                                {/* Solo (Kantor Cabang) */}
                                <div className={styles.officeCard}>
                                    <div className={styles.officeMapWrap}>
                                        <iframe
                                            src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=Jl+Samratulangi+72+A+Manahan+Solo+Jawa+Tengah&zoom=15"
                                            className={styles.mapIframe}
                                            allowFullScreen={false}
                                            loading="lazy"
                                            referrerPolicy="no-referrer-when-downgrade"
                                        />
                                    </div>
                                    <div className={styles.officeInfo}>
                                        <span className={styles.officeBadge}>Kantor Cabang</span>
                                        <h4 className={styles.officeCity}>Solo</h4>
                                        <p className={styles.officeAddress}>Jl. Samratulangi 72 A, Manahan-Solo, Jawa Tengah</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Toaster position="bottom-right" />
            </section>
        </>
    );
}
